import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, withStyles, Slider, Tooltip } from '@material-ui/core';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { IconButton, IconTooltip } from 'components';
import { TEXT_TO_SPEECH } from 'constants';
import { Typography } from '@redshelf/redshelf-ui';

const useStyles = makeStyles((theme) => {
  return {
    /* Styles applied to the root element. */
    root: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'left',
      justifyContent: 'center',
      gap: theme.spacing(0.5),
    },
    speedContainer: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
    },
    speedLabel: {
      paddingLeft: theme.spacing(1),
    },
    tooltip: {
      minHeight: theme.spacing(6.5),
      minWidth: theme.spacing(10.75),
      paddingLeft: theme.spacing(1.5),
      fontSize: theme.typography.pxToRem(14),
      whiteSpace: 'pre',
    },
    tooltipPopper: {
      zIndex: '1300',
    },
    hiddenSliderTooltipContainer: {
      clip: 'rect(0 0 0 0)',
      clipPath: 'inset(50%)',
      height: '1px',
      overflow: 'hidden',
      position: 'absolute',
      whiteSpace: 'nowrap',
      width: '1px',
    },
    iconButton: {
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.shelfBlue[700]}`,
        outlineOffset: theme.spacing(0.25),
      },
    },
  };
});

const TTSSlider = withStyles((theme) => {
  return {
    root: {
      maxWidth: '100%',
      padding: theme.spacing(2.625, 0),
    },
    thumb: {
      height: theme.spacing(1.5),
      width: theme.spacing(1.5),
      marginTop: theme.spacing(-0.75),
      marginLeft: theme.spacing(-0.75),
      backgroundColor: theme.palette.shelfBlue[500],
      transition: 'backgroundColor 0.2s',
      outlineOffset: theme.spacing(1.875),
      '&:hover': {
        boxShadow: '0 0px 0px 10px rgba(0, 113, 219, 0.16)',
      },
      '&:active': {
        boxShadow: '0 0px 0px 15px rgba(0, 113, 219, 0.16)',
      },
      '&:focus': {
        boxShadow: '0 0px 0px 15px rgba(0, 113, 219, 0.16)',
        outline: '2px solid #003D75',
      },
    },
    track: {
      height: theme.spacing(0.5),
      borderRadius: theme.spacing(0.5),
      transform: 'translateY(-1px)',
      backgroundColor: theme.palette.shelfBlue[500],
      transition: 'backgroundColor 0.2s',
    },
    rail: {
      height: theme.spacing(0.25),
      backgroundColor: theme.palette.shelfBlue[300],
      borderRadius: theme.spacing(0.125),
      transition: 'backgroundColor 0.2s',
    },
    markLabel: {
      marginTop: theme.spacing(2.5),

      '&[data-index="0"]': {
        transform: 'translateX(0%)',
      },

      '&[data-index="1"]': {
        transform: 'translateX(-100%)',
      },
    },
    mark: {
      display: 'none',
    },
    marked: {
      marginBottom: 0,
    },
    markActive: {
      backgroundColor: theme.palette.shelfBlue[700],
    },
  };
})(Slider);

const getSpeed = (rate) => {
  return `${rate.toFixed(1)}x`;
};

const getSpeedAndWPM = (rate) => {
  const rateText = getSpeed(rate);
  const rateWPM = `${(rate * 200).toFixed(0)} WPM`;

  return `${rateText} \n${rateWPM}`;
};

const getVolumeTooltip = (value) => {
  if (value === 0.5) {
    return 'Speed Min';
  } else if (value === 4) {
    return 'Speed Max';
  } else {
    return (
      <>
        Speed changed to{' '}
        <span className="noTranslate">{getSpeedAndWPM(value)}</span>
      </>
    );
  }
};

const ValueLabelComponent = ({ open, children, value }) => {
  const classes = useStyles();
  const tooltipValue = (
    <span className="noTranslate">{getSpeedAndWPM(value)}</span>
  );

  return (
    <Tooltip
      classes={{ tooltip: classes.tooltip, popper: classes.tooltipPopper }}
      open={open}
      enterDelay={0}
      placement="top"
      title={tooltipValue}
      arrow
    >
      {children}
    </Tooltip>
  );
};

const TTSSpeed = ({
  currentRate = TEXT_TO_SPEECH.DEFAULTS.RATE,
  onRateChange = () => {},
  isReadyForTooltip,
  ...props
}) => {
  const classes = useStyles();

  const handleChange = (e, newValue) => {
    onRateChange(newValue);
  };

  const handleOnSpeedDown = () => {
    if (currentRate > 0.5) {
      const newValue = currentRate - 0.1;
      onRateChange(+newValue.toFixed(1));
    }
  };

  const handleOnSpeedUp = () => {
    if (currentRate < 4) {
      const newValue = currentRate + 0.1;
      onRateChange(+newValue.toFixed(1));
    }
  };

  return (
    <div className={classes.root} {...props}>
      <div
        id="hiddenSpeedTooltip"
        aria-atomic="true"
        aria-live="assertive"
        className={classes.hiddenSliderTooltipContainer}
      >
        {getVolumeTooltip(currentRate)}
      </div>
      <Typography className={classes.speedLabel} id="speed-control">
        Speed
      </Typography>
      <div className={classes.speedContainer}>
        <IconTooltip
          disabled={currentRate === 0.5}
          id="decrease-speed"
          title="Decrease Speed"
        >
          <IconButton
            aria-label="Decrease Speed"
            className={classes.iconButton}
            onClick={handleOnSpeedDown}
            disabled={currentRate === 0.5}
          >
            <RemoveCircleIcon />
          </IconButton>
        </IconTooltip>
        <TTSSlider
          value={currentRate}
          onChange={handleChange}
          step={0.1}
          min={0.5}
          max={4}
          aria-labelledby="speed-control"
          aria-valuetext={`${getSpeed(currentRate)} speed`}
          marks={TEXT_TO_SPEECH.OPTIONS.MARKS}
          {...(isReadyForTooltip
            ? {
                ValueLabelComponent: ValueLabelComponent,
                valueLabelDisplay: 'on',
              }
            : {})}
        />
        <IconTooltip
          disabled={currentRate === 4}
          id="increase-speed"
          title="Increase Speed"
        >
          <IconButton
            aria-label="Increase Speed"
            className={classes.iconButton}
            onClick={handleOnSpeedUp}
            disabled={currentRate === 4}
          >
            <AddCircleIcon />
          </IconButton>
        </IconTooltip>
      </div>
    </div>
  );
};

TTSSpeed.propTypes = {
  currentRate: PropTypes.number,
  onRateChange: PropTypes.func,
  isReadyForTooltip: PropTypes.bool,
};

export default TTSSpeed;
